// Attachment thresholds and constants
export const ATTACHMENT_THRESHOLDS = {
  MIN_ROWS: 50,
  MAX_INLINE_SIZE_BYTES: 50 * 1024, // 50KB
  PREVIEW_ROWS: 10
}

export const ATTACHMENT_TYPES = {
  QUERY_RESULTS: 'application/json',
  JSX_CODE: 'application/jsx',
  CSV_DATA: 'text/csv',
  MARKDOWN: 'text/markdown',
  IMAGE: 'image/*',
  DQL_QUERY: 'text/dql',
  SQL_QUERY: 'text/sql',
  ARTIFACT_EMAIL: 'application/vnd.denali.artifact.email',
  ARTIFACT_AGENDA: 'application/vnd.denali.artifact.agenda',
  ARTIFACT_TALKING_POINTS: 'application/vnd.denali.artifact.talking_points',
  ARTIFACT_PROPOSAL: 'application/vnd.denali.artifact.proposal',
  ARTIFACT_SUMMARY: 'application/vnd.denali.artifact.summary',
  ARTIFACT_QUERY_RESULTS: 'application/vnd.denali.artifact.query_results',
  TOOL_CALL: 'application/vnd.denali.tool-call'
}

// Check if data should be displayed as attachment
export function shouldCreateAttachment (data) {
  if (!data) return false

  // For arrays, check row count
  if (Array.isArray(data)) {
    return data.length > ATTACHMENT_THRESHOLDS.MIN_ROWS
  }

  // For other data, estimate size
  try {
    const sizeEstimate = JSON.stringify(data).length
    return sizeEstimate > ATTACHMENT_THRESHOLDS.MAX_INLINE_SIZE_BYTES
  } catch (error) {
    // If we can't stringify, probably too complex to display inline
    return true
  }
}

// Extract attachment metadata from a message
export function extractAttachmentMetadata (message) {
  const attachments = []

  // Check for query results that should be attachments
  if (message.data?.results && shouldCreateAttachment(message.data.results)) {
    if (Array.isArray(message.data.results || [])) {
      const results = (message.data.results || [])
      const columns = Object.keys(results[0] || {})

      attachments.push({
        attachmentId: `inline_${message.threadMessageId}_results`,
        threadId: message.threadId,
        threadMessageId: message.threadMessageId,
        title: 'Query Results',
        contentType: ATTACHMENT_TYPES.QUERY_RESULTS,
        preview: {
          rowCount: results.length,
          columns: columns,
          sampleRows: (results || []).slice(0, ATTACHMENT_THRESHOLDS.PREVIEW_ROWS)
        },
        inline: true, // Flag indicating data is embedded in message
        data: results
      })
    }
  }

  // Check for code blocks that might be JSX
  if (message.data?.code && message.data.code.language === 'jsx') {
    attachments.push({
      attachmentId: `inline_${message.threadMessageId}_code`,
      threadId: message.threadId,
      threadMessageId: message.threadMessageId,
      title: message.data.code.filename || 'Code',
      contentType: ATTACHMENT_TYPES.JSX_CODE,
      inline: true,
      data: message.data.code.content
    })
  }

  // Add any stored attachments from the message
  if (message.attachments && Array.isArray(message.attachments)) {
    // Map backend attachment structure to frontend structure
    const backendAttachments = message.attachments.map(att => {
      // Normalize content types that might come from backend
      let contentType = att.contentType
      if (contentType === 'text/sql') {
        contentType = ATTACHMENT_TYPES.DQL_QUERY
      }

      return {
        attachmentId: att.threadMessageAttachmentId || att.attachmentId,
        threadId: att.threadId || message.threadId,
        threadMessageId: att.threadMessageId || message.threadMessageId,
        title: att.title,
        contentType,
        data: att.data,
        inline: !!att.data // If data is included, it's inline
      }
    })
    attachments.push(...backendAttachments)
  }

  return attachments
}

// Get icon name for attachment type
export function getAttachmentIcon (contentType) {
  switch (contentType) {
    case ATTACHMENT_TYPES.QUERY_RESULTS:
      return 'viewListConfig' // table icon
    case ATTACHMENT_TYPES.JSX_CODE:
      return 'code'
    case ATTACHMENT_TYPES.DQL_QUERY:
    case ATTACHMENT_TYPES.SQL_QUERY:
      return 'dataObject' // database/query icon
    case ATTACHMENT_TYPES.CSV_DATA:
      return 'insertDriveFile'
    case ATTACHMENT_TYPES.MARKDOWN:
      return 'description'
    case ATTACHMENT_TYPES.ARTIFACT_EMAIL:
      return 'mail'
    case ATTACHMENT_TYPES.ARTIFACT_AGENDA:
      return 'event'
    case ATTACHMENT_TYPES.ARTIFACT_TALKING_POINTS:
      return 'formatListBulleted'
    case ATTACHMENT_TYPES.ARTIFACT_PROPOSAL:
      return 'description'
    case ATTACHMENT_TYPES.ARTIFACT_SUMMARY:
      return 'summarize'
    case ATTACHMENT_TYPES.TOOL_CALL:
      return 'build'
    default:
      return 'attachment'
  }
}

// Format attachment preview text
export function getAttachmentPreviewText (attachment) {
  if (attachment.contentType === ATTACHMENT_TYPES.QUERY_RESULTS && attachment.preview) {
    return `${attachment.preview.rowCount.toLocaleString()} rows`
  }

  if (attachment.sizeBytes) {
    return formatFileSize(attachment.sizeBytes)
  }

  // Return type labels for artifacts
  const artifactLabels = {
    [ATTACHMENT_TYPES.ARTIFACT_EMAIL]: 'Email Draft',
    [ATTACHMENT_TYPES.ARTIFACT_AGENDA]: 'Meeting Agenda',
    [ATTACHMENT_TYPES.ARTIFACT_TALKING_POINTS]: 'Talking Points',
    [ATTACHMENT_TYPES.ARTIFACT_PROPOSAL]: 'Proposal',
    [ATTACHMENT_TYPES.ARTIFACT_SUMMARY]: 'Summary',
    [ATTACHMENT_TYPES.TOOL_CALL]: 'Tool Execution'
  }

  if (artifactLabels[attachment.contentType]) {
    return artifactLabels[attachment.contentType]
  }

  return attachment.title
}

// Format file size for display
export function formatFileSize (bytes) {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Export data without file-saver
export function exportData (data, filename, format = 'csv') {
  let content
  let mimeType

  switch (format.toLowerCase()) {
    case 'json':
      content = JSON.stringify(data, null, 2)
      mimeType = 'application/json'
      break

    case 'csv':
    default:
      // Use papaparse to generate CSV
      if (window.Papa) {
        content = window.Papa.unparse(data)
        mimeType = 'text/csv'
      } else {
        // Fallback to simple CSV generation
        content = convertToSimpleCSV(data)
        mimeType = 'text/csv'
      }
      break
  }

  // Create blob and download
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')

  a.href = url
  a.download = `${filename}.${format}`
  document.body.appendChild(a)
  a.click()

  // Cleanup
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// Simple CSV converter fallback
function convertToSimpleCSV (data) {
  if (!data || data.length === 0) return ''

  const headers = Object.keys(data[0])
  const rows = [
    headers.join(','),
    ...data.map(row =>
      headers.map(header => {
        const value = row[header]
        // Escape values containing commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`
        }
        return value
      }).join(',')
    )
  ]

  return rows.join('\n')
}
